import React from 'react';
import PageHelmet from '../../../components/shared/PageHelmet';
import NavHeader from '../../../components/NavHeader/NavHeader';
import MainFooter from '../../../components/MainFooter/MainFooter';
import IndustryHero from '../../../components/IndustryHero/IndustryHero';
import IndustryChallenge from '../../../components/IndustryChallenge/IndustryChallenge';
import IndustryProductSpotlight from '../../../components/IndustryProductSpotlight/IndustryProductSpotlight';
import IndustryTechStack from '../../../components/IndustryTechStack/IndustryTechStack';
import OurExpertise from '../../../components/OurExpertise/OurExpertise';
import KeyFeatures from '../../Offerings/KeyFeatures/KeyFeatures';
import CommonCtaArea from '../../../components/CommonCtaArea/CommonCtaArea';
import { ShoppingCart, CalendarCheck, Store, Truck, Globe, TrendingUp, Smartphone, Terminal, Database, PaintBucket, UtensilsCrossed, Building2, MapPinned, ClipboardList, Heart } from 'lucide-react';

const ChallengeItems = [
   'Menu coordination',
   'Customer enquiries',
   'Event bookings',
   'Bulk orders',
   'Vendor availability',
   'Quotations',
   'Payments',
   'Delivery',
   'Customer communication',
   'Marketing',
];

const WhatWeBuildData = [
   { id: 1, icon: <ShoppingCart size="64" strokeWidth='1' />, title: 'Food Ordering Platforms', content: 'Create convenient digital ordering experiences for customers.' },
   { id: 2, icon: <CalendarCheck size="64" strokeWidth='1' />, title: 'Catering Platforms', content: 'Manage events, menus, quotations, customers, and vendors.' },
   { id: 3, icon: <Store size="64" strokeWidth='1' />, title: 'Vendor Management', content: 'Give food vendors tools to manage menus, availability, orders, and quotations.' },
   { id: 4, icon: <Truck size="64" strokeWidth='1' />, title: 'Delivery Platforms', content: 'Support ordering, dispatch, tracking, and delivery workflows.' },
   { id: 5, icon: <Globe size="64" strokeWidth='1' />, title: 'Food Business Websites & Apps', content: 'Build modern digital experiences across web and mobile.' },
   { id: 6, icon: <TrendingUp size="64" strokeWidth='1' />, title: 'Digital Marketing', content: 'Help food brands increase visibility, attract customers, and generate orders.' },
];

const TechData = [
   { icon: <Smartphone size={32} strokeWidth={1.5} />, name: 'React Native', descriptor: '' },
   { icon: <Globe size={32} strokeWidth={1.5} />, name: 'React', descriptor: '' },
   { icon: <Terminal size={32} strokeWidth={1.5} />, name: 'Python', descriptor: '' },
   { icon: <Database size={32} strokeWidth={1.5} />, name: 'PostgreSQL', descriptor: '' },
   { icon: <PaintBucket size={32} strokeWidth={1.5} />, name: 'UI/UX', descriptor: '' },
   { icon: <TrendingUp size={32} strokeWidth={1.5} />, name: 'Digital Marketing & SEO', descriptor: '' },
];

const SolutionsData = [
   { id: 1, icon: <UtensilsCrossed size="42" strokeWidth='1' />, title: 'Food Ordering Apps', content: '' },
   { id: 2, icon: <CalendarCheck size="42" strokeWidth='1' />, title: 'Catering Platforms', content: '' },
   { id: 3, icon: <Store size="42" strokeWidth='1' />, title: 'Restaurant Applications', content: '' },
   { id: 4, icon: <Building2 size="42" strokeWidth='1' />, title: 'Vendor Marketplaces', content: '' },
   { id: 5, icon: <MapPinned size="42" strokeWidth='1' />, title: 'Event Food Platforms', content: '' },
   { id: 6, icon: <Truck size="42" strokeWidth='1' />, title: 'Delivery Platforms', content: '' },
   { id: 7, icon: <ClipboardList size="42" strokeWidth='1' />, title: 'Menu Management Systems', content: '' },
   { id: 8, icon: <Heart size="42" strokeWidth='1' />, title: 'Customer Loyalty Platforms', content: '' },
];

const FoodCatering = () => {
   return (
      <>
         <PageHelmet
            pageTitle="Food & Catering Software Solutions"
            description="Digital solutions for food, catering, and hospitality businesses — from ordering and menu management to event bookings and delivery operations."
            keywords="Food Delivery App Development, Catering Platform Development, Harsoft Solutions"
            path="/industries/food-catering"
         />

         <NavHeader />
         <main>
            <IndustryHero
               title="Digital Solutions for Food, Catering & Hospitality Businesses"
               intro="From ordering and menu management to event bookings and delivery operations, we build technology that simplifies food-service businesses."
               description="Harsoft combines mobile, web, backend, database, UI/UX, and digital marketing capabilities to create better food-service experiences."
               primaryCta={{ label: 'Build Your Food Platform', to: '/contact' }}
            />

            <IndustryChallenge
               title="Industry Challenges"
               intro="Food and catering businesses often manage:"
               items={ChallengeItems}
               outro="Harsoft helps bring these workflows into one connected digital experience."
            />

            <OurExpertise
               title="What We Build"
               subtitle="Solutions Built for Food & Catering Businesses"
               content="From ordering to delivery, we build the platforms food-service businesses run on."
               OurExpertiseData={WhatWeBuildData}
            />

            <IndustryTechStack items={TechData} />

            <IndustryProductSpotlight
               name="Bulk Food Delivery"
               tagline="A Digital Platform for Bulk Food Ordering & Catering"
               description="The platform simplifies:"
               bullets={['Event booking', 'Menu selection', 'Menu customization', 'Vendor management', 'Quotations', 'Orders', 'Payments', 'Notifications', 'Delivery coordination']}
               ctaLabel="Explore Bulk Food Delivery"
               ctaLink="/contact"
            />

            <KeyFeatures KeyFeaturesData={SolutionsData} title="Solutions We Can Build" />

            <CommonCtaArea
               title={<>Ready to Digitize Your Food Business?</>}
               subtitle="Let's build an experience that makes ordering, managing, and growing your food business easier."
               buttonText="Build Your Food Platform"
               buttonLink="/contact"
            />
         </main>
         <MainFooter />
      </>
   );
};

export default FoodCatering;
